import { DEFAULT_PAGE_SIZE, Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import { PolicyModelV1 } from '../models/policy-model-v1/policy-v1.model';
import { JsonObject } from '../helpers';
import { flatten, orderBy, uniq } from 'lodash';
import {
  CreateResponse,
  DEFAULT_SERVICE_ERROR,
  DeleteResponse,
  FieldError,
  ListResponse,
  PatchResponse,
  SAFE_LIMIT,
} from '.';
import { ContextProviderMeta } from '../models/context-provider';
import { getProvidersByIds } from './context-provider.service';
import { PrivateModelModel } from '../models/private-model/private-model.model';
import { ContextProviderModel } from '../models/context-provider/context-provider.model';
import { getPrivateModelsByIds } from './private-model.service';
import { AgentMeta, AgentModel } from '../models/agent.model';
import { getAgentsByIds } from './agent.service';
import { create, deleteApi, get, patch } from './sp-api.service';
import { AxiosError } from 'axios';

type ServiceFieldError = {
  field: string;
  rule: string;
};

export type PolicyMetaResult = [
  Map<string, ContextProviderMeta>,
  Map<string, PrivateModelModel>,
  Map<string, AgentMeta>,
];

export const getGroupPoliciesV1 = async (
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<PolicyModelV1>> => {
  const response = (await get('/policies', {
    page: 1,
    pageSize: SAFE_LIMIT,
    scope: 'group',
  })) as ListResponse;

  if (!response) {
    return {
      page,
      pageSize,
      rows: [],
      total: 0,
    };
  }

  let rows: PolicyModelV1[] = response.data
    .filter(({ scope }) => scope === 'group')
    .map((data) => new PolicyModelV1(data));

  if (dtFilter) {
    const { values, keyword } = dtFilter;

    if (keyword) {
      const safeKeyword = String(keyword).toLowerCase();
      rows = rows.filter(({ name }) => name.toLowerCase().includes(safeKeyword));
    }

    values.forEach(({ columnName, value }) => {
      switch (columnName) {
        case 'groupIds':
          rows = rows.filter(({ groups }) =>
            groups.some((groupId) => (value as string[]).includes(groupId))
          );
          break;
      }
    });
  }

  // Sort
  if (dtSort) {
    const { columnName, direction } = dtSort;
    rows = orderBy(rows, [columnName], [direction]);
  }

  const total = rows.length;

  // Paginate
  const startIndex = page * pageSize;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return {
    page,
    pageSize,
    rows,
    total,
  };
};

export const getAllPoliciesV1 = async (): Promise<PolicyModelV1[]> => {
  const response = (await get('/policies', { page: 1, pageSize: SAFE_LIMIT })) as ListResponse;
  return response?.data ? response.data.map((data) => new PolicyModelV1(data)) : [];
};

export const getAllGroupPoliciesV1 = async (): Promise<PolicyModelV1[]> => {
  return getGroupPoliciesV1(0, SAFE_LIMIT).then(({ rows }) => rows);
};

export const getPolicyV1ById = async (policyId: string): Promise<PolicyModelV1 | null> => {
  const response = await get(`/policies/${policyId}`);
  return response ? new PolicyModelV1(response as JsonObject) : null;
};

export const getPoliciesV1ByIds = async (policyIds: string[]): Promise<PolicyModelV1[]> => {
  return getAllPoliciesV1().then((policies) =>
    policies.filter(({ id }) => policyIds.includes(id))
  );
};

export const getPoliciesByGroupIdsV1 = async (
  groupIds: string[]
): Promise<PolicyModelV1[]> => {
  return getGroupPoliciesV1(0, SAFE_LIMIT, undefined, {
    keyword: '',
    fields: [],
    values: [{ columnName: 'groupIds', value: groupIds }],
  }).then(({ rows }) => rows);
};

export const getOrgPolicyV1 = async (): Promise<PolicyModelV1 | null> => {
  const response = (await get('/policies', { page: 1, pageSize: 1, scope: 'org' })) as ListResponse;
  return response?.data?.length === 1 ? new PolicyModelV1(response.data[0]) : null;
};

export const getPoliciesByModelIdsV1 = async (
  modelIds: string[]
): Promise<PolicyModelV1[]> => {
  const policies = await getAllPoliciesV1();

  return policies.filter(
    ({ portal }) =>
      portal?.availablePrivateModelIds?.some((id) => modelIds.includes(id)) ||
      modelIds.includes(portal?.defaultModelId || '')
  );
};

export const getPoliciesByDatasourceIdsV1 = async (
  datasourceIds: string[]
): Promise<PolicyModelV1[]> => {
  const policies = await getAllPoliciesV1();

  return policies.filter(({ contextDataSources }) =>
    contextDataSources?.enabled?.some((id) => datasourceIds.includes(id))
  );
};

export const getPoliciesByAgentIdsV1 = async (
  agentIds: string[]
): Promise<PolicyModelV1[]> => {
  const policies = await getAllPoliciesV1();

  return policies.filter(({ assistants }) =>
    assistants?.enabled?.some((id) => agentIds.includes(id))
  );
};

export const getPoliciesByServiceIdV1 = async (
  serviceId: string
): Promise<PolicyModelV1[]> => {
  const policies = await getAllPoliciesV1();
  return policies.filter(({ allowsPublicServices, publicServices: { enabled, monitored } }) => {
    return allowsPublicServices || enabled?.includes(serviceId) || monitored?.includes(serviceId);
  });
};

/*
 * Note that this function does not have the same validation found in patchPolicyV1 because the only
 * way to create a group policy at the moment is to first define just the name of the policy, save
 * it with all default settings, and then patch it afterwards
 */
export const createGroupPolicyV1 = async (
  policy: PolicyModelV1
): Promise<CreateResponse> => {
  const orgPolicy = await getOrgPolicyV1();
  if (orgPolicy?.sensitiveData) {
    const { detect, action } = orgPolicy.sensitiveData;
    policy.sensitiveData.detect = detect;
    policy.sensitiveData.action = action;
  }

  const createData = {
    ...policy._props,
    scope: 'group',
    description: null, // @todo can be removed once the policy service validation for this field is updated
    id: undefined,
    _lastChangedAt: undefined,
  };

  const response = await create('/policies', createData);

  if (!response.created) {
    return response;
  }

  response.model = new PolicyModelV1(response.data as JsonObject);

  return response;
};

export const patchPolicyV1 = async (
  policyId: string,
  data: JsonObject
): Promise<PatchResponse> => {
  return patch(`/policies/${policyId}`, data, handlePatchErrorResponse);
};

export const deletePolicyV1 = async (policyId: string): Promise<DeleteResponse> => {
  return deleteApi(`/policies/${policyId}`);
};

export const getPolicyMetaV1 = async (
  policies: PolicyModelV1[]
): Promise<PolicyMetaResult> => {
  const providerIds = flatten(
    policies.map(({ contextDataSources }) => contextDataSources?.enabled || [])
  );

  const privateModelIds = flatten(
    policies.map(({ portal }) => portal?.availablePrivateModelIds || [])
  );

  const agentIds = uniq(flatten(policies.map(({ assistants }) => assistants?.enabled || [])));

  if (!providerIds.length && !privateModelIds.length && !agentIds.length) {
    return [new Map(), new Map(), new Map()];
  }

  const [providers, models, agents] = await Promise.all([
    providerIds.length ? getProvidersByIds(providerIds) : [],
    privateModelIds.length ? getPrivateModelsByIds(privateModelIds) : [],
    agentIds.length ? getAgentsByIds(agentIds) : [],
  ]);

  const contextMap: Map<string, ContextProviderMeta> = new Map();

  (providers as ContextProviderModel[]).forEach((provider) => {
    contextMap.set(provider.id, provider.meta);
  });

  const modelMap: Map<string, PrivateModelModel> = new Map();

  (models as PrivateModelModel[]).forEach((model) => {
    modelMap.set(model.id, model);
  });

  const agentMap: Map<string, AgentMeta> = new Map();

  (agents as AgentModel[]).forEach((agents) => {
    agentMap.set(agents.id, agents.meta);
  });

  return [contextMap, modelMap, agentMap];
};

const handlePatchErrorResponse = (error: AxiosError): PatchResponse => {
  if (!error.response) {
    return { patched: false, error: DEFAULT_SERVICE_ERROR };
  }

  const { status, data } = error.response as { status: number; data: JsonObject };

  if (status === 400 && Array.isArray(data.errors)) {
    return { patched: false, fieldErrors: getFieldErrors(data.errors as ServiceFieldError[]) };
  }

  return { patched: false, error: DEFAULT_SERVICE_ERROR };
};

const getFieldErrors = (errors: ServiceFieldError[]): FieldError[] => {
  return errors.map(({ field, rule }) => ({
    type: 'validation',
    value: null,
    msg: rule,
    path: field,
    location: 'body',
  }));
};
